import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { saveData } from 'services/api/saveData';
import { encryptData } from 'services/authentication/encryptData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faCalendarAlt, faFileInvoice, faRocket } from '@fortawesome/free-solid-svg-icons';
import TextField from 'components/forms/fields/TextField';
import { v4 as uuidv4 } from 'uuid';
import DropdownField from 'components/forms/fields/DropdownField';
import logoBlack from 'assets/crafter-logo-black.png';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import { useGlobalContext } from 'GlobalContext';
import { fetchData } from 'services/api/fetchData';
import '../../style/scss/wizard.scss';
import '../../style/scss/account.scss';
import '../../style/scss/forms.scss';

const TrialWizard: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const uuid = useMemo(() => uuidv4(), []);
    const { setShowFooterLogo, setErrorMessages } = useGlobalContext();
    const { environmentHash, setRedirectTo, handleLogout } = useAuthContext();
    const { step } = useParams<{ step: string }>();
    const currentStep = parseInt(step || '1');
    const [companyName, setCompanyName] = useState<string>('');
    const [industry, setIndustry] = useState<string>('');
    const [companySize, setCompanySize] = useState<string>('');
    const [trialType, setTrialType] = useState('');
    const [createDemoData, setCreateDemoData] = useState(true);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [progress, setProgress] = useState<number>(5);
    const [pollCount, setPollCount] = useState(0);

    // Hide the footer logo and restore it when leaving the wizard
    useEffect(() => {
        setShowFooterLogo(false);
        return () => setShowFooterLogo(true);
    }, []);

    // Start polling for demo data creation status
    useEffect(() => {
        if (currentStep !== 3) return;

        const interval = setInterval(async () => {
            try {
                // Fetch the status
                const response = await fetchData({ apiUrl: 'get_trial_demo_data_status', handleLogout });

                // Update poll count and progress
                setPollCount(prevCount => {
                    const newCount = prevCount + 1;

                    setProgress(prev => {
                        const backendProgress = response.progress || 0;

                        // Prevent backwards update
                        let next = Math.max(prev, backendProgress);

                        // Jump to 20% on second poll
                        if (newCount >= 2 && prev < 20) {
                            next = 20;
                        }

                        return next;
                    });

                    return newCount;
                });

                // Handle successful creation
                if (response.status === 'created') {
                    clearInterval(interval);

                    // Store the changed redirect in the local storage for hard refreshes
                    setRedirectTo('trial-homepage');
                    localStorage.setItem('redirect_to', encryptData(JSON.stringify('trial-homepage')));

                    // Redirect to the trial home page
                    history.push('/trial-homepage');
                }

                // Handle unsuccessful creation
                if (response.status === 'failed') {
                    clearInterval(interval)

                    // Store the changed redirect in the local storage for hard refreshes
                    setRedirectTo('trial-homepage');
                    localStorage.setItem('redirect_to', encryptData(JSON.stringify('trial-homepage')));

                    // Redirect to the trial home page
                    history.push('/trial-homepage');
                }

                // Handle skipped demo data creation, the environment is ready right away
                if (response.status === 'idle' || response.status === 'deleted') {
                    clearInterval(interval)

                    // Jump the progress bar to completed
                    setProgress(100);

                    // Store the changed redirect in the local storage for hard refreshes
                    setRedirectTo('trial-homepage');
                    localStorage.setItem('redirect_to', encryptData(JSON.stringify('trial-homepage')));

                    // Redirect to the trial home page after a short delay
                    setTimeout(() => history.push('/trial-homepage'), 1200);
                }
            } catch (e) {
                console.error(e)
            }
        }, 1500);

        return () => clearInterval(interval);
    }, [currentStep]);

    // Set the company size options
    const companySizeOptions = [
		{ value: '1', name: 'trial.size.freelance' },
        { value: '2-5', name: 'trial.size.2-5' },
        { value: '6-10', name: 'trial.size.6-10'},
        { value: '11-25', name: 'trial.size.11-25' },
        { value: '26-50', name: 'trial.size.26-50' },
        { value: '51+', name: 'trial.size.50+' },
	];

    // Set the industry options
    const industryOptions = [
        { value: 'installation_technical', name: 'trial.industry.installation_technical' },
        { value: 'security_access', name: 'trial.industry.security_access' },
        { value: 'blinds', name: 'trial.industry.blinds' },
        { value: 'interior_finishing', name: 'trial.industry.interior_finishing' },
        { value: 'construction_renovation', name: 'trial.industry.construction_renovation' },
        { value: 'equipment_machinery', name: 'trial.industry.equipment_machinery' },
        { value: 'signage_visual', name: 'trial.industry.signage_visual' },
        { value: 'landscaping_outdoor', name: 'trial.industry.landscaping_outdoor' },
        { value: 'facility_services', name: 'trial.industry.facility_services' },
        { value: 'other', name: 'trial.industry.other' },
    ]

    // Set the feature options
	const trialTypeOptions = [
        {
            type: 'starter',
            icon: faCalendarAlt,
            title: 'trial.trial_type.starter.name',
            description: 'trial.trial_type.starter.description',
        },
        {
            type: 'professional',
            icon: faFileInvoice,
            title: 'trial.trial_type.professional.name',
            description: 'trial.trial_type.professional.description',
        },
        {
            type: 'advanced',
            icon: faRocket,
            title: 'trial.trial_type.advanced.name',
            description: 'trial.trial_type.advanced.description',
        },
    ]

    // Handle company name change
    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setCompanyName(e.target.value);

        if (e.target.value.trim() !== '') {
            setErrorMessages((prev: any) => ({ ...prev, ['company_name']: undefined }));
        }
    };

    // Handle trial type selection
    const handleTrialTypeSelection = (value: string) => {
        setTrialType(value)

        setErrorMessages((prev: any) => ({ ...prev, trial_type: undefined }))
    }

    // Navigate to the next step
	const nextStep = () => {
        const isNameEmpty = companyName.trim() === '';
        const isSizeEmpty = companySize.trim() === '';
    
        // Show error if company name is not entered
        if (isNameEmpty) {
            setErrorMessages((prev: any) => ({ ...prev, ['company_name']: 'trial.general.invalid_company_name' }));
        }

        // Show error if company size is not selected
        if (isSizeEmpty) {
            setErrorMessages((prev: any) => ({ ...prev, ['company_size']: 'trial.general.invalid_company_size' }));
        }
    
        // Only go to the next step if the fields are filled
        if (!isNameEmpty && !isSizeEmpty) {
            history.push(`/trial-wizard/${currentStep + 1}`);
        }
	};

	// Navigate to the previous step
	const prevStep = () => {
		if (currentStep > 1) {
			history.push(`/trial-wizard/${currentStep - 1}`);
		}
	};

    // Handle the final submit
	const handleSubmit = async () => {
        // Start the button loader
        setButtonLoader(true);

		// Send the data to the backend here
		const trialData = {
            'company_name': companyName,
            'company_size': companySize,
            'industry': industry,
            'trial_type': trialType,
            'create_demo_data': createDemoData,
            'wizard_done': true
        };

        try {
            const saveResponse = await saveData({ 
                apiUrl: `patch_trial/${environmentHash}`, method: 'patch', data: trialData
            });

            // On successful save, redirect to the loading page
            if (saveResponse?.status === 200) {
                history.push('/trial-wizard/3')
            }
        } catch (error) {
            console.error('Saving not possible', error);
            throw error;
        } finally {
            setButtonLoader(false);
        }
	};

    return (
        <div className='wizard trial-wizard'>
            <div className='logo-container'>
                <img src={logoBlack} alt="Logo" className='trial-logo'/>
            </div>

            {/* Step 1: Company Details */}
            {currentStep === 1 && (
                <div className='wizard-step company-details'>
                    <div>
                        <div className='header'>
                            <h1>
                                {t('trial.general.company_details_header')}
                            </h1>
                            <p className='subtitle'>
                                {t('trial.general.company_details_subtitle')}
                            </p>
                        </div>
                        <div className='step-content'>
                            <TextField
                                type='text'
                                data={{ company_name: companyName }}
                                viewKey={uuid}
                                label='trial.general.company_name_form_label'
                                name='company_name'
                                placeholder='trial.general.company_name_placeholder'
                                onChange={(e) => handleCompanyNameChange(e)}
                                viewInEditMode={true}
                            />
                            <DropdownField
                                type='dropdown'
                                hardcodedOptions={companySizeOptions}
                                data={{ company_size: companySize}}
                                viewKey={uuid}
                                name='company_size'
                                label='trial.general.company_size_label'
                                disabled_selected='trial.general.select_company_size_disabled_selected'
                                allowNoneOption={false}
                                showSearch={false}
                                onChange={(e) => {
                                    setCompanySize(e.target.value);
                                    setErrorMessages((prev: any) => ({ ...prev, ['company_size']: undefined }));
                                }}
                                viewInEditMode={true}
                            />
                            <DropdownField
                                type='dropdown'
                                hardcodedOptions={industryOptions}
                                data={{ industry: industry}}
                                viewKey={uuid}
                                name='industry'
                                label='trial.general.industry_label'
                                disabled_selected='trial.general.select_industry_disabled_selected'
                                allowNoneOption={false}
                                showSearch={false}
                                onChange={(e) => setIndustry(e.target.value)}
                                viewInEditMode={true}
                            />
                        </div>
                        <div className='button-footer'>
                            <div className='step-counter'>
                                {t('wizard.general.step_counter', { currentStep: currentStep, totalSteps: 2 })}
                            </div>
                            <PrimaryButton
                                label='general.next'
                                size='medium'
                                icon={<FontAwesomeIcon icon={faAngleRight} />}
                                onClick={() => nextStep()} 
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2: Features selection */}
            {currentStep === 2 && (
                <div className='wizard-step'>
                    <div className='container-small'
                        style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className='header'>
                            <h1>
                                {t('trial.general.type_header')}
                            </h1>
                            <p className='subtitle'>
                                {t('trial.general.type_subtitle')}
                            </p>
                        </div>
                    </div>
                    <div className='step-content'>
                        <div className='library-cards'>
                            {trialTypeOptions.map((card, cardIndex) => {
                                return (
                                    <div 
                                        key={cardIndex}
                                        onClick={() => handleTrialTypeSelection(card.type)}
                                        className={`library-card ${trialType === card.type ? 'selected' : ''} `}
                                    >
                                        <div className='icon'>
                                            <FontAwesomeIcon icon={card.icon} />
                                        </div>
                                        <div className='title'>
                                            <h5>{t(card.title)}</h5>
                                        </div>
                                        <div className='description'>
                                            <p>{t(card.description)}</p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div className='demo-data-opt-in'
                             style={{ margin: '1.5rem auto 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <div className='checkbox-wrapper'>
                                <input
                                    type='checkbox'
                                    id='create_demo_data'
                                    name='create_demo_data'
                                    checked={createDemoData}
                                    onChange={(e) => setCreateDemoData(e.target.checked)}
                                    className='hidden-checkbox'
                                />
                                <label htmlFor='create_demo_data'
                                    className={`custom-checkbox ${createDemoData ? 'checked' : ''}`}>
                                    {createDemoData &&
                                        <img src={CheckboxCheck} alt="CheckboxCheck" className='checkbox-check' />
                                    }
                                </label>
                            </div>
                            <label htmlFor='create_demo_data' style={{ cursor: 'pointer', margin: 0 }}>
                                {t('trial.general.create_demo_data_checkbox_label')}
                            </label>
                        </div>
                    </div>
                    <div className='button-footer'
                         style={{ width: '100%', maxWidth: '500px' }}>
                        <SecondaryButton
                            label='general.previous'
                            size='medium'
                            onClick={() => prevStep()} 
                        />
                        <div className='step-counter'>
                            {t('wizard.general.step_counter', { currentStep: currentStep, totalSteps: 2 })}
                        </div>
                        <PrimaryButton
                            label='trial.general.getting_started_button_label'
                            size='medium'
                            icon={<FontAwesomeIcon icon={faAngleRight} />}
                            loading={buttonLoader}
                            onClick={() => handleSubmit()} 
                        />
                    </div>
                </div>
            )}

            {/* Step 3: Data creation status */}
            {currentStep === 3 && (
                <div className='wizard-step trial-setup'>
                    <div className='container-small'>
                        <div className='header'>
                            <h1>
                                {t('trial.general.setup_environment_header')}
                            </h1>
                            <p className='subtitle'>
                                {t('trial.general.setup_environment_subtitle')}
                            </p>
                        </div>

                        <div className='creation-status'>
                            <div className="loader" />
                            <h5 className='dynamic-text'>
                                {progress < 20 && t('trial.general.progress_setup_message')}
                                {progress >= 20 && progress < 40 && t('trial.general.progress_master_data_message')}
                                {progress >= 40 && progress < 60 && t('trial.general.progress_sample_contacts_message')}
                                {progress >= 60 && progress < 95 && t('trial.general.progress_sample_jobs_message')}
                                {progress >= 95 && t('trial.general.progress_almost_done_message')}
                            </h5>
                        </div>

                        <div className='demo-data-progress-bar'>
                            <div style={{ width: `${progress}%` }} className='done' />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrialWizard;